import { getUser as getLocalUser } from 'Service/localUser'
import {
  FILTERS_SEPARATOR,
  ACCEPTABLE_FILE_MAX_CONTENT_LENGTH,
  KB,
  MB,
  GB,
  TB,
  MERGE_FIELDS,
} from 'Constants'

export * from './filters'
export * from './tables'
export * from './urls'

export const applyOpacityToHex = (color, opacity) => {
  const safeOpacity = Math.min(Math.max(opacity, 0), 1)
  const hexOpacity = Math.round(safeOpacity * 255).toString(16)

  return color.slice(0, 7) + hexOpacity
}
export const parseHashInURL = (hash) =>
  hash
    .substring(1)
    .split('&')
    .reduce((acc, keyValue) => {
      const [key, value] = keyValue.split('=')
      acc[key] = value
      return acc
    }, {})

export const createOffice365OauthUrl = (scope) => `
  https://login.microsoftonline.com/common/oauth2/v2.0/authorize?
  client_id=${process.env.REACT_APP_OFFICE365_OAUTH2_CLIENT_ID}&
  response_type=code&
  redirect_uri=${process.env.REACT_APP_OFFICE365_OAUTH2_REDIRECT_URL}&
  response_mode=query&
  scope=${scope || 'user.read'}
`

export const queryStringToObject = (string) => {
  const pairs = string.slice(1).split('&')
  return pairs.reduce((acc, keyValue) => {
    const [key, ...value] = keyValue.split('=')
    if (key) {
      acc[key] = decodeURIComponent(value.join('=') || '')
    }
    return acc
  }, {})
}

export const uriPathToObject = (string) => {
  const queryString = string.split('?').pop()
  return queryStringToObject(`?${queryString}`)
}

export const parseParamsToQueryString = (params) =>
  Object.keys(params).reduce((acc, name) => {
    let value = params[name]
    if (value instanceof Object) {
      value = Object.keys(value)
        .map((v) => `${v}=${value[v]}`)
        .join(FILTERS_SEPARATOR)
    }
    if (value) {
      acc += `${acc ? '&' : ''}${name}=${encodeURIComponent(value)}`
    }
    return acc
  }, '')

export const getUserParamsForIntercom = () => {
  const user = getLocalUser()
  const { id: user_id, contactMethods = [], firstName, lastName, companyName = {} } = user
  const name = `${firstName || ''} ${lastName || ''}`
  const phone = (
    contactMethods.find(({ type: { name: phoneType } }) => phoneType === 'Phone') || {}
  ).value
  const email = (
    contactMethods.find(({ type: { name: emailType } }) => emailType === 'UserPrimaryEmail') || {}
  ).value
  const company = companyName && companyName.name
  return {
    name,
    email,
    phone,
    user_id,
    alignment: 'right',
    company,
  }
}

export const isPdfLink = (link = '', guid = '') =>
  link?.split(guid)[1]?.split('?')[0].toLowerCase() === '.pdf'

export const isHtmlLink = (link = '', guid = '') => {
  return link?.split(guid)[1]?.split('?')[0].toLowerCase() === '.html'
}

export const adjustedFileSize = (size) => {
  if (size > TB) {
    return { adjustedSize: (size / TB).toFixed(1), units: 'TB' }
  }
  if (size > GB) {
    return { adjustedSize: (size / GB).toFixed(1), units: 'GB' }
  }
  if (size > MB) {
    return { adjustedSize: (size / MB).toFixed(1), units: 'MB' }
  }

  return { adjustedSize: (size / KB).toFixed(1), units: 'KB' }
}

export const getRejectedFileContentLengthError = (file) => {
  const { size, type } = file

  const { adjustedSize, units } = adjustedFileSize(size)
  const min = ACCEPTABLE_FILE_MAX_CONTENT_LENGTH[type].MIN_STRING
  const max = ACCEPTABLE_FILE_MAX_CONTENT_LENGTH[type].MAX_STRING
  return `File size (${adjustedSize} ${units}) should be between ${min} KB and ${max} MB`
}

export const capitalize = (string) => {
  if (!string) {
    return ''
  }
  const parts = string.split(' ')
  const capitalized = parts.map((part) => {
    return part[0].toUpperCase() + part.slice(1)
  })

  return capitalized.join(' ')
}

export const selectDate = (setValue) => (fieldName) => (date) => {
  setValue(fieldName, date.toISOString())
}

export const selectField =
  (setValue) =>
  (fieldName) =>
  ({ value }) => {
    setValue(fieldName, value)
  }

export const getSelectDefault = (value) => {
  return value ? { label: capitalize(value), value } : value
}

export const formatCurrencyString = (string) => {
  if (!string) {
    return ''
  }
  const stripped = string.replace(/[^0-9.,]/g, '') || 0
  return parseFloat(stripped).toFixed(2).toString()
}

export const mergeUpdates = (current, source, key) => {
  if (source || MERGE_FIELDS.includes(key) || !current) {
    return key === 'orders' ? [source] : source
  }
  return current
}

export const getDates = (attributes) => {
  const checkIn = attributes?.find(({ name }) => name === 'startdate')?.value
  const checkOut = attributes?.find(({ name }) => name === 'enddate')?.value
  return { checkIn, checkOut }
}

export const currencyFormatter = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    // maximumFractionDigits: 2,
    // minimumFractionDigits: 0,
  })
    .format(value)
    .replace('$', '')
    .trim()
}
